import React, {RefObject, useContext} from "react";
import {EditContext} from "./EditProvider";
import {Column, TID} from "./Table";
import {css} from "@emotion/css";
import {blue, grey} from "@mui/material/colors";
import {ColumnConfig} from "../../api/TableConfig";
import {useDragDrop} from "./DragDrop";

export function TableViewEditableCell<T extends TID>(props: {
    value: T
    dataRef: RefObject<T[]>;
    modified: RefObject<{
        index: number;
        value: T;
    }[]>;
    indexOffset: number;
    index: number;
    autoGrow: boolean;
    isLast: boolean;
    column: Column<T>
    config: ColumnConfig;
    rangeSelected: {min: number; max: number} | undefined;
}) {
    const ctx = useContext(EditContext);
    const {index, rangeSelected, autoGrow, modified, config} = props
    const adjustedIndex = index + props.indexOffset
    const isRangeSelected = !!rangeSelected && rangeSelected.min <= adjustedIndex && rangeSelected.max >= adjustedIndex;
    const isRightOfRange = isRangeSelected && rangeSelected && adjustedIndex === rangeSelected.max;
    const isTopOfRange = isRangeSelected && ctx.onRange.lastValue?.ids.indexOf(props.value.id) === 0;

    const drag = useDragDrop({
        column: props.column,
        value: props.value,
    });

    const modifiedItem = modified.current?.findLast(m => m.index === index);

    return (
        <div
            onDragOver={drag.onDragOver}
            onDragEnter={drag.onDragEnter}
            onDragLeave={drag.onDragLeave}
            onDrop={drag.onDrop}
            onClick={e => {
                e.stopPropagation()

                // sometimes the text get's selected
                document.getSelection()?.removeAllRanges()

                if (e.shiftKey && (ctx.onSelect.lastValue || ctx.onRange.lastValue)) {
                    let lastId = 0;
                    let lastIndex = 0;

                    const range = ctx.onRange.lastValue;
                    const select = ctx.onSelect.lastValue;
                    if (range) {
                        lastId = range.fromId;
                        lastIndex = range.fromIndex;
                    } else if (select) {
                        lastId = select.id;
                        lastIndex = select.index;
                        ctx.onSelect.emit(null);
                    }

                    let ids: number[] = [];
                    let record = false;

                    if (lastId === props.value.id) {
                        ids.push(lastId);
                    } else {
                        props.dataRef.current?.map((row: T) => {
                            if (row.id === lastId || row.id === props.value.id) {
                                if (!record) {
                                    record = true;
                                } else {
                                    ids.push(row.id);
                                    record = false
                                }
                            }

                            if (record) {
                                ids.push(row.id);
                            }

                            return null;
                        })
                    }

                    ctx.onRange.emit({
                        fromId: lastId,
                        fromIndex: lastIndex,
                        toId: props.value.id,
                        toIndex: index + props.indexOffset,
                        ids: ids,
                    })

                    return;
                }

                ctx.onRange.emit(null);
                ctx.onSelect.emit({
                    id: props.value.id,
                    index: index + props.indexOffset,
                    value: props.value,
                    editing: false,
                });
            }}
            className={editCell + " " + (isRangeSelected ? rangeSelectedCell : standardCell)}
            data-col={props.column.name}
            style={{
                width: autoGrow ? undefined : config.width,
                flex: autoGrow ? 1 : undefined,
                minWidth: config.width,
                textAlign: props.column.alignRight ? "right" : undefined,
                borderRight: props.isLast ? "none" : (isRangeSelected && !isRightOfRange) ? "1px solid transparent" : undefined,
                borderTop: (isRangeSelected && !isTopOfRange) ? "1px solid transparent" : undefined,
                backgroundColor:
                    !!modifiedItem ? grey["200"] :
                    drag.isDraggingOver ? blue["300"] :
                    drag.isLighthouse ? blue["50"] :
                        ctx.cellCustomize?.backgroundColor?.(props.value, props.column) || undefined,
                whiteSpace: "pre-wrap",
            }}
        >
            {props.column.render(modifiedItem?.value || props.value, props.column) || <>&nbsp;</>}
        </div>
    )
}

const rangeSelectedCell = css({
    border: "1px solid " + blue["600"],
})

const standardCell = css({
    border: "1px solid transparent",
    borderRight: "1px solid " + grey["300"],
})

const editCell = css({
    position: "relative",
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 3,
    paddingTop: 3,
    fontSize: "0.8rem",
    cursor: "pointer",
    lineHeight: "1em",
    wordBreak: "break-all",

    "&:hover": {
        border: "1px solid " + blue["600"],
    }
})